import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Drawer,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ArticleIcon from '@mui/icons-material/Article';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { DndContext, PointerSensor, TouchSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { uploadImage } from '../utils/uploadImage';
import { RECIPE_TYPES } from '../constants/Constants';
import { refreshRecipes } from '../utils/refreshRecipes';
import { fetchDrafts, createDraft, updateDraft, deleteDraft } from '../utils/draftsApi';
import { SortableIngredientRow, SortableDirectionRow, SortableSection, SectionReorderModal } from '../components/SortableRows';
import ImportFromUrlDialog from '../components/ImportFromUrlDialog';
import LinkIcon from '@mui/icons-material/Link';

const DRAFT_LIMIT = 5;

const RECIPE_INIT = {
    title: '',
    authors: [],
    prep_time: '',
    recipe_yield: '',
    recipe_description: '',
    recipe_type_id: '',
    uploaded_image: '',
    source_url: '',
    ingredient_categories: [
        { name: '', ingredients: [{ ingredient: '', quantity: '', unit: '' }] }
    ],
    direction_categories: [
        { name: '', directions: [''] }
    ],
};

const formatDraftDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getDraftName = (recipe) => {
    const date = formatDraftDate(new Date().toISOString());
    return recipe.title?.trim() ? `${recipe.title.trim()} — ${date}` : `Draft — ${date}`;
};

// ── Drafts panel (shared between sidebar and mobile drawer) ──────────────────

const DraftsList = ({ drafts, activeDraftId, onLoad, onDelete, atLimit }) => (
    <Box>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5, fontSize: 10, px: 2 }}>
            My Drafts {drafts.length > 0 && `(${drafts.length}/${DRAFT_LIMIT})`}
        </Typography>
        {drafts.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, mt: 1 }}>
                No drafts yet. Start typing to auto-save.
            </Typography>
        )}
        {atLimit && (
            <Alert severity="warning" sx={{ mx: 2, mt: 1, py: 0.5, fontSize: 12 }}>
                Limit reached. Delete a draft to save new ones.
            </Alert>
        )}
        <Box sx={{ mt: 1 }}>
            {drafts.map(draft => (
                <Box
                    key={draft.id}
                    onClick={() => onLoad(draft)}
                    sx={{
                        px: 2, py: 1.25, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1,
                        bgcolor: activeDraftId === draft.id ? 'action.selected' : 'transparent',
                        borderLeft: activeDraftId === draft.id ? '3px solid' : '3px solid transparent',
                        borderColor: activeDraftId === draft.id ? 'primary.main' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' },
                    }}
                >
                    <ArticleIcon fontSize="small" color={activeDraftId === draft.id ? 'primary' : 'disabled'} sx={{ flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: activeDraftId === draft.id ? 700 : 400 }} noWrap>
                            {draft.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {formatDraftDate(draft.date_modified)}
                        </Typography>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); onDelete(draft); }}
                        sx={{ flexShrink: 0, color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            ))}
        </Box>
    </Box>
);

// ── Main component ────────────────────────────────────────────────────────────

const AddRecipe = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [searchParams, setSearchParams] = useSearchParams();

    const [recipe, setRecipe] = useState(RECIPE_INIT);
    const [errors, setErrors] = useState({});
    const [showSuccess, setShowSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const previewUrlRef = useRef(null);

    const [drafts, setDrafts] = useState([]);
    const [activeDraftId, setActiveDraftId] = useState(null);
    const [saveStatus, setSaveStatus] = useState(null); // 'saving' | 'saved' | 'error' | 'limit'
    const [draftsDrawerOpen, setDraftsDrawerOpen] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const saveTimerRef = useRef(null);
    const isFirstRender = useRef(true);
    const [crossSectionToast, setCrossSectionToast] = useState(false);
    const [reorderModal, setReorderModal] = useState(null); // 'ingredients' | 'directions' | null
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [authorOptions, setAuthorOptions] = useState([]);

    const dndSensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    );

    const handleIngredientDragEnd = (catIndex, event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const [acat] = active.id.split('-');
        const [ocat] = over.id.split('-');
        if (acat !== ocat) { setCrossSectionToast(true); return; }
        const cat = recipe.ingredient_categories[catIndex];
        const oldIdx = cat.ingredients.findIndex((_, i) => `${catIndex}-ing-${i}` === active.id);
        const newIdx = cat.ingredients.findIndex((_, i) => `${catIndex}-ing-${i}` === over.id);
        const updated = recipe.ingredient_categories.map((c, ci) =>
            ci === catIndex ? { ...c, ingredients: arrayMove(c.ingredients, oldIdx, newIdx) } : c
        );
        setRecipe({ ...recipe, ingredient_categories: updated });
    };

    const handleIngredientCategoryDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIdx = recipe.ingredient_categories.findIndex((_, i) => `ing-cat-${i}` === active.id);
        const newIdx = recipe.ingredient_categories.findIndex((_, i) => `ing-cat-${i}` === over.id);
        setRecipe({ ...recipe, ingredient_categories: arrayMove(recipe.ingredient_categories, oldIdx, newIdx) });
    };

    const handleDirectionCategoryDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIdx = recipe.direction_categories.findIndex((_, i) => `dir-cat-${i}` === active.id);
        const newIdx = recipe.direction_categories.findIndex((_, i) => `dir-cat-${i}` === over.id);
        setRecipe({ ...recipe, direction_categories: arrayMove(recipe.direction_categories, oldIdx, newIdx) });
    };

    const handleDirectionDragEnd = (catIndex, event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const [acat] = active.id.split('-');
        const [ocat] = over.id.split('-');
        if (acat !== ocat) { setCrossSectionToast(true); return; }
        const cat = recipe.direction_categories[catIndex];
        const oldIdx = cat.directions.findIndex((_, i) => `${catIndex}-dir-${i}` === active.id);
        const newIdx = cat.directions.findIndex((_, i) => `${catIndex}-dir-${i}` === over.id);
        const updated = recipe.direction_categories.map((c, ci) =>
            ci === catIndex ? { ...c, directions: arrayMove(c.directions, oldIdx, newIdx) } : c
        );
        setRecipe({ ...recipe, direction_categories: updated });
    };

    useEffect(() => {
        const headers = { Authorization: 'Bearer ' + Cookies.get('token') };
        Promise.all([
            fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/authors`, { headers }).then(r => r.json()),
            fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/users`, { headers }).then(r => r.json()),
        ]).then(([authors, usersData]) => {
            const authorOpts = (authors || []).map(a => ({ id: a.id, name: a.name, type: 'author' }));
            const userOpts = (usersData.users || []).map(u => ({
                id: u.id,
                name: u.display_name || `${u.first_name} ${u.last_name}`.trim(),
                type: 'user',
            }));
            setAuthorOptions([...authorOpts, ...userOpts]);
        }).catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Load drafts on mount; auto-load oldest if ?loadOldest=1
    useEffect(() => {
        fetchDrafts().then(drafts => {
            setDrafts(drafts);
            if (searchParams.get('loadOldest') === '1' && drafts.length > 0) {
                const oldest = drafts[drafts.length - 1];
                setRecipe({ ...RECIPE_INIT, ...oldest.content });
                setActiveDraftId(oldest.id);
                isFirstRender.current = true;
                setSearchParams({}, { replace: true });
            }
        }).catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        return () => {
            if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        };
    }, []);

    // Auto-save debounced on recipe change
    useEffect(() => {
        if (isFirstRender.current) { isFirstRender.current = false; return; }

        const isEmpty = !recipe.title?.trim() && (!recipe.authors || recipe.authors.length === 0) &&
            recipe.ingredient_categories.every(c => c.ingredients.every(i => !i.ingredient?.trim())) &&
            recipe.direction_categories.every(c => c.directions.every(d => !d?.trim()));
        if (isEmpty) return;

        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        setSaveStatus('saving');

        saveTimerRef.current = setTimeout(async () => {
            try {
                const name = getDraftName(recipe);
                const content = { ...recipe, uploaded_image: null }; // can't serialize File objects
                if (activeDraftId) {
                    const updated = await updateDraft(activeDraftId, { name, content });
                    setDrafts(prev => prev.map(d => d.id === activeDraftId ? updated : d));
                } else {
                    if (drafts.length >= DRAFT_LIMIT) { setSaveStatus('limit'); return; }
                    const created = await createDraft(name, content);
                    setActiveDraftId(created.id);
                    setDrafts(prev => [created, ...prev]);
                }
                setSaveStatus('saved');
            } catch {
                setSaveStatus('error');
            }
        }, 2000);
    }, [recipe]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadDraft = (draft) => {
        setRecipe({ ...RECIPE_INIT, ...draft.content });
        setActiveDraftId(draft.id);
        setDraftsDrawerOpen(false);
        setSaveStatus(null);
        isFirstRender.current = true;
    };

    const handleImportRecipes = async (dataList) => {
        const created = [];
        for (const data of dataList) {
            try {
                const name = data.title?.trim()
                    ? `${data.title.trim()} — Imported`
                    : `Imported — ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                const draft = await createDraft(name, data);
                created.push(draft);
                setDrafts(prev => [draft, ...prev]);
            } catch (e) {
                // skip drafts that fail (e.g. limit hit mid-batch)
            }
        }
        if (created.length > 0) {
            const first = created[0];
            setRecipe({ ...RECIPE_INIT, ...first.content });
            setActiveDraftId(first.id);
            setSaveStatus(null);
            isFirstRender.current = true;
        }
    };

    const handleDeleteDraft = async (draft) => {
        setDrafts(prev => prev.filter(d => d.id !== draft.id));
        if (activeDraftId === draft.id) {
            setActiveDraftId(null);
            setRecipe(RECIPE_INIT);
            isFirstRender.current = true;
        }
        setDeleteConfirm(null);
        try {
            await deleteDraft(draft.id);
        } catch {
            setDrafts(prev => [draft, ...prev].sort((a, b) => new Date(b.date_modified) - new Date(a.date_modified)));
            setSaveStatus('error');
        }
    };

    // ── Field change handlers ────────────────────────────────────────────────

    const handleCategoryNameChange = (value, type, catIndex) => {
        const key = type === 'ingredients' ? 'ingredient_categories' : 'direction_categories';
        const updated = recipe[key].map((cat, i) => i === catIndex ? { ...cat, name: value } : cat);
        setRecipe({ ...recipe, [key]: updated });
    };

    const handleIngredientChange = (value, field, catIndex, index) => {
        const updated = recipe.ingredient_categories.map((cat, ci) => {
            if (ci !== catIndex) return cat;
            const ingredients = cat.ingredients.map((ing, ii) => ii === index ? { ...ing, [field]: value } : ing);
            return { ...cat, ingredients };
        });
        setRecipe({ ...recipe, ingredient_categories: updated });
    };

    const handleDirectionChange = (value, catIndex, index) => {
        const updated = recipe.direction_categories.map((cat, ci) => {
            if (ci !== catIndex) return cat;
            const directions = cat.directions.map((d, di) => di === index ? value : d);
            return { ...cat, directions };
        });
        setRecipe({ ...recipe, direction_categories: updated });
    };

    const addIngredient = (catIndex) => {
        const updated = recipe.ingredient_categories.map((cat, ci) =>
            ci === catIndex ? { ...cat, ingredients: [...cat.ingredients, { ingredient: '', quantity: '', unit: '' }] } : cat
        );
        setRecipe({ ...recipe, ingredient_categories: updated });
    };

    const addDirection = (catIndex) => {
        const updated = recipe.direction_categories.map((cat, ci) =>
            ci === catIndex ? { ...cat, directions: [...cat.directions, ''] } : cat
        );
        setRecipe({ ...recipe, direction_categories: updated });
    };

    const addIngredientCategory = () => setRecipe({
        ...recipe,
        ingredient_categories: [...recipe.ingredient_categories, { name: '', ingredients: [{ ingredient: '', quantity: '', unit: '' }] }],
    });

    const addDirectionCategory = () => setRecipe({
        ...recipe,
        direction_categories: [...recipe.direction_categories, { name: '', directions: [''] }],
    });

    const deleteIngredient = (catIndex, index) => {
        const updated = recipe.ingredient_categories.map((cat, ci) =>
            ci === catIndex ? { ...cat, ingredients: cat.ingredients.filter((_, ii) => ii !== index) } : cat
        );
        setRecipe({ ...recipe, ingredient_categories: updated });
    };

    const deleteDirection = (catIndex, index) => {
        const updated = recipe.direction_categories.map((cat, ci) =>
            ci === catIndex ? { ...cat, directions: cat.directions.filter((_, di) => di !== index) } : cat
        );
        setRecipe({ ...recipe, direction_categories: updated });
    };

    const deleteIngredientCategory = (catIndex) => setRecipe({
        ...recipe,
        ingredient_categories: recipe.ingredient_categories.filter((_, i) => i !== catIndex),
    });

    const deleteDirectionCategory = (catIndex) => setRecipe({
        ...recipe,
        direction_categories: recipe.direction_categories.filter((_, i) => i !== catIndex),
    });

    // ── Submit ───────────────────────────────────────────────────────────────

    const handleSaveChanges = async () => {
        setErrors({});
        const newErrors = {};
        if (!recipe.title) newErrors.title = 'Recipe Name is required';
        if (!recipe.authors || recipe.authors.length === 0) newErrors.author = 'At least one author is required';
        if (!recipe.recipe_type_id) newErrors.recipe_type = 'Please select a Recipe Type';

        const hasIngredient = recipe.ingredient_categories.some(cat =>
            (cat.ingredients || []).some(ing => ing.ingredient?.trim())
        );
        if (!hasIngredient) newErrors.ingredients = 'At least one ingredient is required';

        const hasDirection = recipe.direction_categories.some(cat =>
            (cat.directions || []).some(d => d?.trim())
        );
        if (!hasDirection) newErrors.directions = 'At least one direction is required';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            const fieldOrder = ['title', 'recipe_type', 'author', 'ingredients', 'directions'];
            const firstError = fieldOrder.find(f => newErrors[f]);
            if (firstError) {
                setTimeout(() => {
                    document.getElementById(`field-${firstError}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 50);
            }
            return;
        }

        setLoading(true);
        setSubmitError(null);

        try {
            const payload = {
                title: recipe.title,
                author: recipe.authors[0]?.display_name || null,
                author_id: recipe.authors[0]?.author_id || null,
                author_user_id: recipe.authors[0]?.author_user_id || null,
                authors: recipe.authors.map((a, i) => ({ ...a, order: i })),
                recipe_type_id: recipe.recipe_type_id ? parseInt(recipe.recipe_type_id, 10) : null,
                prep_time: recipe.prep_time || null,
                recipe_yield: recipe.recipe_yield || null,
                recipe_description: recipe.recipe_description || null,
                source_url: recipe.source_url || null,
                ingredients: recipe.ingredient_categories.flatMap(cat =>
                    (cat.ingredients || [])
                        .filter(ing => ing?.ingredient?.trim())
                        .map(ing => ({
                            ingredient: ing.ingredient,
                            quantity: ing.quantity || null,
                            unit: ing.unit || null,
                            category_name: cat.name || null,
                        }))
                ),
                directions: (() => {
                    const dirs = [];
                    let order = 1;
                    for (const cat of recipe.direction_categories) {
                        for (const d of (cat.directions || []).filter(dd => dd?.trim())) {
                            dirs.push({ direction: d, direction_order: order++, category_name: cat.name || null });
                        }
                    }
                    return dirs;
                })(),
            };

            if (recipe.uploaded_image) {
                await new Promise((resolve, reject) => {
                    uploadImage(recipe.uploaded_image,
                        (error) => reject(error),
                        (imageData) => { payload.image_url = imageData[0]; payload.download_image_url = imageData[1]; resolve(); }
                    );
                });
            }

            const response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/addrecipe`, {
                method: 'POST',
                headers: { 'Authorization': 'Bearer ' + Cookies.get('token'), 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to add recipe');
            }

            // Delete the draft on successful submit
            if (activeDraftId) {
                await deleteDraft(activeDraftId).catch(() => {});
                setDrafts(prev => prev.filter(d => d.id !== activeDraftId));
                setActiveDraftId(null);
            }

            setShowSuccess(true);
            if (previewUrlRef.current) { URL.revokeObjectURL(previewUrlRef.current); previewUrlRef.current = null; }
            setRecipe({ ...RECIPE_INIT });
            setErrors({});
            isFirstRender.current = true;
            refreshRecipes(true);
        } catch (err) {
            setSubmitError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const draftsPanel = (
        <DraftsList
            drafts={drafts}
            activeDraftId={activeDraftId}
            onLoad={loadDraft}
            onDelete={setDeleteConfirm}
            atLimit={drafts.length >= DRAFT_LIMIT}
        />
    );

    const form = (
        <>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                {/* Title row */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            {activeDraftId ? 'Edit Draft' : 'Add Recipe'}
                        </Typography>
                        {activeDraftId && (
                            <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.5 }}>
                                {drafts.find(d => d.id === activeDraftId)?.name}
                            </Typography>
                        )}
                    </Box>
                    {/* Right side: New Recipe (desktop) + save status */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, pt: 0.5 }}>
                        {!isMobile && (
                            <Button size="small" variant="outlined" startIcon={<LinkIcon />}
                                onClick={() => setImportDialogOpen(true)}
                            >
                                Import from URL
                            </Button>
                        )}
                        {!isMobile && activeDraftId && (
                            <Button size="small" variant="outlined" startIcon={<AddCircleOutlineIcon />}
                                onClick={() => {
                                    setRecipe(RECIPE_INIT);
                                    setActiveDraftId(null);
                                    setSaveStatus(null);
                                    isFirstRender.current = true;
                                }}
                            >
                                New Recipe
                            </Button>
                        )}
                        {saveStatus === 'saving' && <Typography variant="caption" color="text.secondary">Saving…</Typography>}
                        {saveStatus === 'saved' && (
                            <Tooltip title="Draft saved">
                                <CheckCircleOutlineIcon fontSize="small" color="success" />
                            </Tooltip>
                        )}
                        {saveStatus === 'limit' && <Chip label="Draft limit reached" size="small" color="warning" />}
                        {saveStatus === 'error' && <Typography variant="caption" color="error">Save failed</Typography>}
                    </Box>
                </Box>
                {/* Mobile-only action buttons row */}
                {isMobile && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                        <Button size="small" startIcon={<ArticleIcon />} onClick={() => setDraftsDrawerOpen(true)}
                            variant={drafts.length > 0 ? 'outlined' : 'text'}
                        >
                            Drafts {drafts.length > 0 && `(${drafts.length})`}
                        </Button>
                        <Button size="small" variant="outlined" startIcon={<LinkIcon />}
                            onClick={() => setImportDialogOpen(true)}
                        >
                            Import URL
                        </Button>
                        {activeDraftId && (
                            <Button size="small" variant="outlined" startIcon={<AddCircleOutlineIcon />}
                                onClick={() => {
                                    setRecipe(RECIPE_INIT);
                                    setActiveDraftId(null);
                                    setSaveStatus(null);
                                    isFirstRender.current = true;
                                }}
                            >
                                New Recipe
                            </Button>
                        )}
                    </Box>
                )}
            </Box>

            {/* Image upload */}
            <Box sx={{ mb: 3, border: '1px dashed', borderColor: 'divider', borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, bgcolor: 'background.paper' }}>
                <input accept="image/*" id="file-input" type="file" style={{ display: 'none' }}
                    onChange={(e) => {
                        const file = e.target.files[0];
                        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
                        previewUrlRef.current = file ? URL.createObjectURL(file) : null;
                        setRecipe({ ...recipe, uploaded_image: file });
                    }}
                />
                {recipe.uploaded_image ? (
                    <>
                        <Box component="img" src={previewUrlRef.current} alt="Preview"
                            sx={{ maxHeight: 200, maxWidth: '100%', borderRadius: 1, objectFit: 'contain' }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 220 }}>
                                {recipe.uploaded_image.name}
                            </Typography>
                            <label htmlFor="file-input">
                                <IconButton component="span" size="small"><EditIcon fontSize="small" /></IconButton>
                            </label>
                        </Box>
                    </>
                ) : (
                    <label htmlFor="file-input">
                        <Button variant="outlined" component="span" startIcon={<AddPhotoAlternateIcon />}>Upload Image</Button>
                    </label>
                )}
            </Box>

            {/* Basic fields */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField id="field-title" label="Recipe Name" variant="outlined" fullWidth value={recipe.title}
                    onChange={(e) => setRecipe({ ...recipe, title: e.target.value })}
                    error={!!errors.title} helperText={errors.title}
                    InputLabelProps={{ shrink: !!recipe.title }} />

                <FormControl id="field-recipe_type" fullWidth error={!!errors.recipe_type}>
                    <InputLabel>Recipe Type</InputLabel>
                    <Select value={recipe.recipe_type_id} onChange={(e) => setRecipe({ ...recipe, recipe_type_id: e.target.value })} label="Recipe Type">
                        <MenuItem value="" disabled>Select Recipe Type</MenuItem>
                        {RECIPE_TYPES.map((type) => (
                            <MenuItem key={type.id} value={type.id}>{type.label}</MenuItem>
                        ))}
                    </Select>
                    {errors.recipe_type && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>{errors.recipe_type}</Typography>
                    )}
                </FormControl>

                <Autocomplete
                    id="field-author"
                    multiple
                    freeSolo
                    options={authorOptions.filter(o => !recipe.authors.some(a => (a.author_id === o.id && o.type === 'author') || (a.author_user_id === o.id && o.type === 'user')))}
                    groupBy={(o) => typeof o === 'object' && o.type !== 'free' ? (o.type === 'author' ? 'Author Profiles' : 'Family Members') : undefined}
                    getOptionLabel={(o) => typeof o === 'string' ? o : o.name}
                    isOptionEqualToValue={(o, v) => {
                        if (typeof o === 'string' || typeof v === 'string') return o === v;
                        if (o.type === 'free' || v.type === 'free') return o.name === v.name;
                        return o.id === v.id && o.type === v.type;
                    }}
                    value={recipe.authors.map(a => {
                        if (a.author_id) return authorOptions.find(o => o.id === a.author_id && o.type === 'author') ?? { id: a.author_id, name: a.display_name, type: 'author' };
                        if (a.author_user_id) return authorOptions.find(o => o.id === a.author_user_id && o.type === 'user') ?? { id: a.author_user_id, name: a.display_name, type: 'user' };
                        return { name: a.display_name, type: 'free' };
                    })}
                    onChange={(_, selected) => {
                        const authors = selected.map((v, i) => ({
                            display_name: typeof v === 'string' ? v : v.name,
                            author_id: typeof v === 'object' && v.type === 'author' ? v.id : null,
                            author_user_id: typeof v === 'object' && v.type === 'user' ? v.id : null,
                            order: i,
                        }));
                        setRecipe(r => ({ ...r, authors }));
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Author(s)"
                            variant="outlined"
                            error={!!errors.author}
                            helperText={errors.author || (recipe.authors.length > 0 ? `${recipe.authors.length} author${recipe.authors.length > 1 ? 's' : ''} added` : '')}
                        />
                    )}
                />

                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <TextField label="Prep Time" variant="outlined" fullWidth value={recipe.prep_time}
                            onChange={(e) => setRecipe({ ...recipe, prep_time: e.target.value })}
                            InputLabelProps={{ shrink: !!recipe.prep_time }} />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField label="Recipe Yield" variant="outlined" fullWidth value={recipe.recipe_yield}
                            onChange={(e) => setRecipe({ ...recipe, recipe_yield: e.target.value })}
                            InputLabelProps={{ shrink: !!recipe.recipe_yield }} />
                    </Grid>
                </Grid>

                <TextField label="Description" variant="outlined" fullWidth multiline rows={3}
                    value={recipe.recipe_description}
                    onChange={(e) => setRecipe({ ...recipe, recipe_description: e.target.value })}
                    InputLabelProps={{ shrink: !!recipe.recipe_description }} />
            </Box>

            {/* Ingredients */}
            <Box id="field-ingredients" sx={{ mt: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Ingredients</Typography>
                    {recipe.ingredient_categories.length > 1 && (
                        <Button size="small" startIcon={<SwapVertIcon />} onClick={() => setReorderModal('ingredients')}>
                            Reorder Sections
                        </Button>
                    )}
                </Box>
                <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleIngredientCategoryDragEnd}>
                    <SortableContext items={recipe.ingredient_categories.map((_, i) => `ing-cat-${i}`)} strategy={verticalListSortingStrategy}>
                        {recipe.ingredient_categories.map((category, catIndex) => (
                            <SortableSection key={`ing-cat-${catIndex}`} id={`ing-cat-${catIndex}`}>
                                <TextField label="Section name (optional)" variant="outlined" fullWidth size="small"
                                    value={category.name}
                                    onChange={(e) => handleCategoryNameChange(e.target.value, 'ingredients', catIndex)}
                                    sx={{ mb: 2 }}
                                    InputProps={recipe.ingredient_categories.length > 1 ? {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => deleteIngredientCategory(catIndex)}><Delete fontSize="small" /></IconButton>
                                            </InputAdornment>
                                        ),
                                    } : undefined}
                                />
                                <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={(e) => handleIngredientDragEnd(catIndex, e)}>
                                    <SortableContext items={category.ingredients.map((_, i) => `${catIndex}-ing-${i}`)} strategy={verticalListSortingStrategy}>
                                        {category.ingredients.map((ingredientObj, index) => (
                                            <SortableIngredientRow
                                                key={`${catIndex}-ing-${index}`}
                                                id={`${catIndex}-ing-${index}`}
                                                ingredientObj={ingredientObj}
                                                index={index}
                                                catIndex={catIndex}
                                                showDelete={category.ingredients.length > 1}
                                                errors={errors}
                                                onChange={handleIngredientChange}
                                                onDelete={deleteIngredient}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                                <Button size="small" onClick={() => addIngredient(catIndex)} sx={{ mt: 0.5 }}>+ Add Ingredient</Button>
                            </SortableSection>
                        ))}
                    </SortableContext>
                </DndContext>
                <Button variant="outlined" size="small" onClick={addIngredientCategory} sx={{ mb: 1 }}>+ Add Section</Button>
            </Box>

            {/* Directions */}
            <Box id="field-directions" sx={{ mt: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Directions</Typography>
                    {recipe.direction_categories.length > 1 && (
                        <Button size="small" startIcon={<SwapVertIcon />} onClick={() => setReorderModal('directions')}>
                            Reorder Sections
                        </Button>
                    )}
                </Box>
                <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDirectionCategoryDragEnd}>
                    <SortableContext items={recipe.direction_categories.map((_, i) => `dir-cat-${i}`)} strategy={verticalListSortingStrategy}>
                        {recipe.direction_categories.map((category, catIndex) => (
                            <SortableSection key={`dir-cat-${catIndex}`} id={`dir-cat-${catIndex}`}>
                                <TextField label="Section name (optional)" variant="outlined" fullWidth size="small"
                                    value={category.name}
                                    onChange={(e) => handleCategoryNameChange(e.target.value, 'directions', catIndex)}
                                    sx={{ mb: 2 }}
                                    InputProps={recipe.direction_categories.length > 1 ? {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => deleteDirectionCategory(catIndex)}><Delete fontSize="small" /></IconButton>
                                            </InputAdornment>
                                        ),
                                    } : undefined}
                                />
                                <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDirectionDragEnd(catIndex, e)}>
                                    <SortableContext items={category.directions.map((_, i) => `${catIndex}-dir-${i}`)} strategy={verticalListSortingStrategy}>
                                        {category.directions.map((direction, dIndex) => (
                                            <SortableDirectionRow
                                                key={`${catIndex}-dir-${dIndex}`}
                                                id={`${catIndex}-dir-${dIndex}`}
                                                direction={direction}
                                                index={dIndex}
                                                catIndex={catIndex}
                                                showDelete={category.directions.length > 1}
                                                errors={errors}
                                                onChange={handleDirectionChange}
                                                onDelete={deleteDirection}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                                <Button size="small" onClick={() => addDirection(catIndex)} sx={{ mt: 0.5 }}>+ Add Step</Button>
                            </SortableSection>
                        ))}
                    </SortableContext>
                </DndContext>
                <Button variant="outlined" size="small" onClick={addDirectionCategory} sx={{ mb: 1 }}>+ Add Section</Button>
            </Box>

            {submitError && <Alert severity="error" sx={{ mt: 3 }}>{submitError}</Alert>}

            <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Button onClick={handleSaveChanges} variant="contained" color="primary" size="large"
                    disabled={loading} sx={{ minWidth: 160 }}>
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Save Recipe'}
                </Button>
            </Box>
        </>
    );

    return (
        <>
            <Snackbar open={showSuccess} autoHideDuration={6000} onClose={(_, reason) => reason !== 'clickaway' && setShowSuccess(false)}>
                <Alert severity="success" variant="filled" sx={{ width: '100%' }} onClose={() => setShowSuccess(false)}>
                    Recipe added successfully!
                </Alert>
            </Snackbar>
            <Snackbar open={crossSectionToast} autoHideDuration={3000} onClose={() => setCrossSectionToast(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity="info" variant="filled" onClose={() => setCrossSectionToast(false)}>
                    Reordering between sections isn't supported — drag within a section only.
                </Alert>
            </Snackbar>

            {isMobile ? (
                <Container component="main" maxWidth="sm" sx={{ mt: 5, pb: 12 }}>
                    {form}
                    <Drawer anchor="bottom" open={draftsDrawerOpen} onClose={() => setDraftsDrawerOpen(false)}
                        PaperProps={{ sx: { borderTopLeftRadius: 12, borderTopRightRadius: 12, pb: 4, pt: 2, maxHeight: '70vh' } }}>
                        {draftsPanel}
                    </Drawer>
                </Container>
            ) : (
                <Box sx={{ display: 'flex', mt: 5, pb: 12 }}>
                    {/* Drafts sidebar */}
                    <Box sx={{ width: 240, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', pt: 1, position: 'sticky', top: 64, height: 'calc(100vh - 64px)', overflowY: 'auto' }}>
                        {draftsPanel}
                    </Box>
                    {/* Form */}
                    <Container component="main" maxWidth="sm" sx={{ flex: 1 }}>
                        {form}
                    </Container>
                </Box>
            )}

            {/* Section reorder modals (mobile only) */}
            <SectionReorderModal
                open={reorderModal === 'ingredients'}
                onClose={() => setReorderModal(null)}
                sections={recipe.ingredient_categories}
                title="Ingredient"
                onApply={(order) => setRecipe({ ...recipe, ingredient_categories: order.map(i => recipe.ingredient_categories[i]) })}
            />
            <SectionReorderModal
                open={reorderModal === 'directions'}
                onClose={() => setReorderModal(null)}
                sections={recipe.direction_categories}
                title="Direction"
                onApply={(order) => setRecipe({ ...recipe, direction_categories: order.map(i => recipe.direction_categories[i]) })}
            />

            {/* Delete confirmation dialog */}
            <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}><DeleteIcon sx={{ color: 'error.main' }} />Delete draft?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">"{deleteConfirm?.name}" will be permanently deleted.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={() => handleDeleteDraft(deleteConfirm)}>Delete</Button>
                </DialogActions>
            </Dialog>

            <ImportFromUrlDialog
                open={importDialogOpen}
                onClose={() => setImportDialogOpen(false)}
                onImport={handleImportRecipes}
                currentDraftCount={drafts.length}
                existingDrafts={drafts}
            />
        </>
    );
};

export default AddRecipe;
